import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons/faCheckCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import Row from "antd/es/row";
import Tag from "antd/es/tag";
import cn from "classnames";
import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared/enum";
import SandboxStatus from "@mapmycustomers/shared/enum/SandboxStatus";
import Sandbox, { SandboxConfig } from "@mapmycustomers/shared/types/Sandbox";
import { LoadingSpinner } from "@mapmycustomers/ui";

import configService from "@app/config/ConfigService";
import Environment from "@app/enum/Environment";
import SandboxIntegrationReAuth from "@app/scene/settings/component/common/SandboxIntegrationReAuth";
import isSandboxCrmValid from "@app/scene/settings/util/isSandboxCrmValid";
import { fetchSandboxAccessToken } from "@app/store/app/actions";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import { getAppUrl } from "@app/util/appUrl";
import useInterval from "@app/util/hook/useInterval";
import { getSandboxStatusDisplayName } from "@app/util/ui";

import { doesIntegrationExist } from "../../store";
import { fetchSandbox } from "../../store/actions";

import BackToMainAppButton from "./component/BackToMainAppButton";
import CloneDataToMainAppButton from "./component/CloneDataToMainAppButton";
import DeleteButton from "./component/DeleteButton";
import EditButton from "./component/EditButton";
import SwitchToSandboxButton from "./component/SwitchToSandboxButton";
import styles from "./SandboxState.module.scss";

interface Props {
  config?: SandboxConfig;
  doesIntegrationExist: boolean;
  onDelete: () => void;
  onEdit: () => void;
  onFetchSandbox: typeof fetchSandbox.request;
  onFetchSandboxAccessToken: typeof fetchSandboxAccessToken.request;
  onReSave: () => void;
  sandbox: Sandbox;
}

const SandboxState: React.FC<Props> = ({
  doesIntegrationExist,
  onDelete,
  onEdit,
  onFetchSandbox,
  onFetchSandboxAccessToken,
  onReSave,
  sandbox: { config = {}, crm, name, status },
}) => {
  const intl = useIntl();
  const isSetupFinished = useMemo(() => {
    return Object.values(config as Record<string, boolean | EntityType[]>).some(
      (value: boolean | EntityType[]) => (Array.isArray(value) ? value.length > 0 : value)
    );
  }, [config]);

  const isOnSandbox = configService.getCurrentEnvironment() === Environment.SANDBOX;
  const isCloning = status === SandboxStatus.CLONING;
  const isDeleting = status === SandboxStatus.DELETING;
  const isErrored = status === SandboxStatus.ERRORED;

  const isCloningOrDeleting = isCloning || isDeleting;

  const redirectToRelatedApp = useCallback(
    (urlGetter: () => string) => {
      onFetchSandboxAccessToken({
        callback: (token) => {
          window.location.href = getAppUrl(urlGetter(), token, "/settings/organization/sandbox");
        },
      });
    },
    [onFetchSandboxAccessToken]
  );

  const handleSwitchToSandboxApp = useCallback(() => {
    redirectToRelatedApp(configService.getBaseSandboxAppUrl);
  }, [redirectToRelatedApp]);

  const handleSwitchToMainApp = useCallback(() => {
    redirectToRelatedApp(configService.getBaseAppUrl);
  }, [redirectToRelatedApp]);

  useInterval(onFetchSandbox, isCloning || isDeleting, 2000);

  const statusName = useMemo(() => {
    if (isDeleting) {
      return intl.formatMessage({
        id: "settings.sandboxEnvironment.state.deletingFromMainApp",
        defaultMessage: "Deletion in progress...",
        description: "Settings Sandbox Environment - state - deleting From Main App",
      });
    }
    if (isCloning) {
      if (isOnSandbox) {
        return intl.formatMessage({
          id: "settings.sandboxEnvironment.state.cloningToMainApp",
          defaultMessage: "Cloning to production in progress...",
          description: "Settings Sandbox Environment - state - cloning To Main App",
        });
      } else {
        return intl.formatMessage({
          id: "settings.sandboxEnvironment.state.cloningFromMainApp",
          defaultMessage: "Cloning to sandbox in progress...",
          description: "Settings Sandbox Environment - state - cloning From Main App",
        });
      }
    }
    if (status === SandboxStatus.ACTIVE && !isOnSandbox) {
      return intl.formatMessage({
        id: "settings.sandboxEnvironment.state.sandboxNotActive",
        defaultMessage: "Not active",
        description: "Settings Sandbox Environment - state - Sandbox is not active",
      });
    }
    if (isErrored || isSetupFinished) {
      return getSandboxStatusDisplayName(intl, status);
    }
    return intl.formatMessage({
      id: "settings.sandboxEnvironment.state.didntFinished",
      defaultMessage: "Setup still required",
      description: "Settings Sandbox Environment - state - Setup still required",
    });
  }, [intl, isDeleting, isErrored, isSetupFinished, isOnSandbox, isCloning, status]);

  const isWarning = !isErrored && !isSetupFinished;

  if (!doesIntegrationExist && isSandboxCrmValid(crm) && config?.integrations && isOnSandbox) {
    return <SandboxIntegrationReAuth showActions />;
  }

  return (
    <div>
      <div
        className={cn(styles.status, {
          [styles.active]: !isErrored && isOnSandbox,
          [styles.cloning]: isCloning,
          [styles.error]: isErrored,
          [styles.warning]: isWarning,
        })}
      >
        <Row align="middle" gutter={layout.spacerM}>
          {isOnSandbox && (
            <Col>
              <FontAwesomeIcon className={styles.sandboxActiveIcon} icon={faCheckCircle} />
            </Col>
          )}
          <Col>{name}</Col>
          <Col>
            <Tag
              className={cn({
                [styles.activeTag]: !isWarning && !isErrored && !isCloning,
                [styles.warningTag]: isWarning,
              })}
            >
              {statusName}
            </Tag>
          </Col>
        </Row>
        <Row align="middle" gutter={layout.spacerM}>
          {!isOnSandbox && !isCloningOrDeleting && (
            <Col>
              <DeleteButton isErrored={isErrored} onClick={onDelete} />
            </Col>
          )}
          {!isErrored && !isOnSandbox && !isCloningOrDeleting && (
            <Col>
              <EditButton onClick={onEdit} />
            </Col>
          )}
          {isSetupFinished && !isOnSandbox && !isCloningOrDeleting && !isErrored && (
            <Col>
              <SwitchToSandboxButton onClick={handleSwitchToSandboxApp} />
            </Col>
          )}
          {isOnSandbox && !isCloningOrDeleting && (
            <Col>
              <BackToMainAppButton onClick={handleSwitchToMainApp} />
            </Col>
          )}
          {isCloning && <LoadingSpinner mini />}
        </Row>
      </div>
      {isOnSandbox && !isCloningOrDeleting && (
        <div>
          <div className={styles.cloneDataToMainApp}>
            {intl.formatMessage({
              id: "settings.sandboxEnvironment.state.cloneDataToMainApp.description.action",
              defaultMessage:
                "To bring your updated sandbox configuration back into your production account, click the button below. Please note that by performing this action, any changes made in your Sandbox configuration will override your production configuration. ",
              description: "Settings Sandbox Environment - state - clone Data To Prod action",
            })}
          </div>
          <CloneDataToMainAppButton onClick={onReSave} />
        </div>
      )}
      {isOnSandbox && isCloning && (
        <div className={styles.cloneDataToMainApp}>
          {intl.formatMessage({
            id: "settings.sandboxEnvironment.state.cloneDataToMainApp.description",
            defaultMessage:
              "We are currently making changes to your main account based. You may see some inconsistencies in the app until the sandbox environment cloning is complete.",
            description: "Settings Sandbox Environment - state - clone Data To Prod description",
          })}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  doesIntegrationExist: doesIntegrationExist(state),
});

const mapDispatchToProps = {
  onFetchSandbox: fetchSandbox.request,
  onFetchSandboxAccessToken: fetchSandboxAccessToken.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(SandboxState);
